/**
 * usePDFExport Hook
 * Handles PDF generation, preview, and download logic for quotations
 * Extracted from QuotationEditorModal for better maintainability
 */
import { useState, useCallback } from "react";
import { toast } from "sonner";
import api from "../lib/axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Custom hook for PDF export functionality
 * @param {Object} quotation - The quotation object with id and code
 * @param {Function} handleSave - Function to save quotation before export
 * @returns {Object} PDF export state and handlers
 */
const usePDFExport = (quotation, handleSave) => {
  // PDF Preview state
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [pdfPreviewBlob, setPdfPreviewBlob] = useState(null);
  const [isPdfPreviewOpen, setIsPdfPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  /**
   * Export PDF - generates PDF and stores for preview
   */
  const handleExportPDF = useCallback(async () => {
    if (!quotation?.id) {
      toast.error("No hay cotización para exportar");
      return;
    }

    setExporting(true);
    try {
      // First save the current state
      if (handleSave) {
        await handleSave();
      }

      // Then generate PDF
      const response = await api.post(
        `${API}/quotations/${quotation.id}/generate-pdf`,
        {},
        { responseType: "blob" }
      );

      // Create URL for preview
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      // Store for preview
      setPdfPreviewBlob(blob);
      setPdfPreviewUrl(url);

      toast.success("PDF generado - Vista previa lista");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Error al generar PDF");
    } finally {
      setExporting(false);
    }
  }, [quotation?.id, handleSave]);

  /**
   * Quick preview - opens the preview modal directly
   */
  const handleQuickPreview = useCallback(async () => {
    if (!quotation?.id) {
      toast.error("No hay cotización para previsualizar");
      return;
    }

    setPreviewLoading(true);
    try {
      // Save first to ensure latest data
      if (handleSave) {
        await handleSave();
      }
      // Open the preview modal - it will fetch the data
      setIsPdfPreviewOpen(true);
    } catch (error) {
      console.error("Error saving before preview:", error);
      toast.error("Error al guardar antes del preview");
    } finally {
      setPreviewLoading(false);
    }
  }, [quotation?.id, handleSave]);

  /**
   * Close API-based PDF preview modal
   */
  const handleClosePdfPreview = useCallback(() => {
    setIsPdfPreviewOpen(false);
  }, []);

  /**
   * Download PDF from preview blob
   */
  const handleDownloadPDF = useCallback(() => {
    if (!pdfPreviewBlob || !quotation?.code) {
      toast.error("No hay PDF para descargar");
      return;
    }

    const url = window.URL.createObjectURL(pdfPreviewBlob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Cotizacion_${quotation.code}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    toast.success("PDF descargado");
  }, [pdfPreviewBlob, quotation?.code]);

  /**
   * Close PDF preview and cleanup URLs
   */
  const closePdfPreview = useCallback(() => {
    if (pdfPreviewUrl) {
      window.URL.revokeObjectURL(pdfPreviewUrl);
    }
    setPdfPreviewUrl(null);
    setPdfPreviewBlob(null);
  }, [pdfPreviewUrl]);

  /**
   * Cleanup function to revoke object URLs on unmount
   */
  const cleanup = useCallback(() => {
    if (pdfPreviewUrl) {
      window.URL.revokeObjectURL(pdfPreviewUrl);
    }
  }, [pdfPreviewUrl]);

  return {
    // State
    pdfPreviewUrl,
    pdfPreviewBlob,
    isPdfPreviewOpen,
    previewLoading,
    exporting,
    // Handlers
    handleExportPDF,
    handleQuickPreview,
    handleClosePdfPreview,
    handleDownloadPDF,
    closePdfPreview,
    cleanup,
    // Setters for external control
    setIsPdfPreviewOpen,
  };
};

export default usePDFExport;
